<template>
  <auth-layout class="authCode" :show-agreement="false">
    <template #preHeader>
      <mq-responsive target="mobile">
        <div class="authCode_preHeader">
          <div class="authCode_close" @click="handleClose">
            <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
              <g clip-path="url(#clip0_20785_78407)">
              <path d="M8 8L20 20M20 8L8 20" stroke="#203454" stroke-width="1.5" stroke-linecap="round"/>
              </g>
              <defs>
              <clipPath id="clip0_20785_78407">
              <rect width="28" height="28" fill="white"/>
              </clipPath>
              </defs>
            </svg>
          </div>
        </div>
      </mq-responsive>
    </template>
    <template #title>
      <div class="authCode_title">{{ $t('auth.code.titleEmail') }}</div>
    </template>
    <template #description>
      <div class="authCode_description">{{ $t('auth.code.descriptionEmail') }}</div>
      <div class="authCode_email">{{ maskedDestinationEmail }} <span @click="handleChangeEmail" class="authCode_email_change">{{ $t('change') }}</span></div>
    </template>
    <template #content>
      <div class="authCode_content">
        <form class="authCode_form" @submit.prevent>
          <div class="authCode_input">
            <InputV3
              v-model="code"
              placeholder="0000"
              :label="$t('auth.code.code')"
              type="number"
              :maxlength="4"
              @input="handleContinue"
              :loading="isLoading"
              autofocus
            />
            <div class="authCode_input_error" v-if="error">{{ error }}</div>
          </div>
          <div>
            <ac-button
              class="authCode_button-continue"
              type="button"
              variant="tonal"
              block
              :disabled="isDisabled"
              @click="handleSendAgain"
            >
              {{ $t('auth.code.getNewCode') }} <span class="authCode_button-continue_timer" v-if="isTimerActive">{{ formatTimerSeconds }}</span>
            </ac-button>
          </div>
        </form>

        <auth-enter-with />

        <ac-button
          variant="text"
          color="gray"
          class="authCode_enter-phone"
          block
          @click="handleEnterPhone"
        >
          {{ $t('auth.enterByPhone') }}
        </ac-button>
      </div>
    </template>
  </auth-layout>
</template>

<script lang="ts" setup>
import AuthLayout from '@web/platform.v3/layouts/auth.vue'
import InputV3 from '@web/platform.v3/components/input.vue'
import AcButton from '@ui-kit/components/AcButton.vue'
import AuthEnterWith from '@web/platform.v3/components/auth/enter-with.vue'
import { MqResponsive } from 'vue3-mq'

import { computed, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from '@web/store'
import PAGE_NAME from '@web/consts/PAGE_NAME'
import { useAuthCatchErrors } from '@web/platform.v3/composables/useAuthCatchErrors'
import useRedirectAfterLogin from '@web/platform.v3/composables/useRedirectAfterLogin'

defineOptions({
  name: 'AuthEmailCode'
})

const { redirectAfterLogin } = useRedirectAfterLogin()

const store = useStore()
const router = useRouter()

const code = ref('')
const error = ref('')
const isLoading = ref(false)
const user = computed(() => store.getters['user/user'])
const storedEmail = computed(() => store.getters['authV3/email'])
const maskedDestinationEmail = computed(() => store.getters['authV3/maskedDestinationEmail'])
const isTimerActive = computed(() => store.getters['authV3/isTimerActive'])
const needStartTimer = computed(() => store.getters['authV3/needStartTimer'])
const timerSeconds = computed(() => store.getters['authV3/timerSeconds'])
const formatTimerSeconds = computed(() => {
  return `00:${timerSeconds.value.toString().padStart(2, '0')}`
})

const isDisabled = computed(() => isTimerActive.value)

const { catchError } = useAuthCatchErrors()

const handleContinue = async () => {
  if (code.value.length !== 4) return
  if (isLoading.value) return
  isLoading.value = true
  try {
    await store.dispatch('authV3/verifyEmailCode', { email: storedEmail.value, code: code.value })
    store.commit('authV3/reset')
    redirectAfterLogin(user.value)
  } catch (e) {
    error.value = catchError(e)
  } finally {
    isLoading.value = false
  }
}

async function handleSendAgain () {
  try {
    await store.dispatch('authV3/requestEmailCode', storedEmail.value)
    store.dispatch('authV3/startTimer', 60)
  } catch (e) {
    error.value = catchError(e)
  }
}
function handleEnterPhone () {
  handleChangeEmail()
  router.replace({ name: 'login', params: { type: 'phone' } })
}

function handleClose () {
  handleChangeEmail()
}

function handleChangeEmail () {
  code.value = ''
  error.value = ''
  store.commit('authV3/setNeedStartTimer', false)
  store.commit('authV3/setEmailStatus', 'email')
}

watch(needStartTimer, (newVal) => {
  if (newVal) {
    store.commit('authV3/setNeedStartTimer', false)
    store.dispatch('authV3/startTimer', timerSeconds.value || 60)
  }
}, {
  immediate: true,
  once: true
})
</script>

<style lang="scss" scoped>
  @import '@web/platform.v3/styles/auth/code.scss';
</style>
